import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  WhiteLabelConfig, 
  fetchWhiteLabelConfigWithOverride, 
  applyWhiteLabelConfig,
  getWhiteLabelLogo,
  setWhiteLabelOverride,
  getWhiteLabelOverride,
  fetchAllWhiteLabelConfigs
} from '@/services/whiteLabelService';

interface WhiteLabelContextType {
  config: WhiteLabelConfig | null;
  isLoading: boolean;
  error: string | null;
  logo: string | null;
  appName: string;
  isWhiteLabeled: boolean;
  refetch: () => Promise<void>;
  // Dev picker functions
  allConfigs: WhiteLabelConfig[];
  loadAllConfigs: () => Promise<void>;
  overrideDomain: string | null;
  setOverride: (domain: string | null) => void;
}

const defaultContext: WhiteLabelContextType = {
  config: null,
  isLoading: true,
  error: null,
  logo: null,
  appName: 'de.iterate',
  isWhiteLabeled: false,
  refetch: async () => {},
  allConfigs: [],
  loadAllConfigs: async () => {},
  overrideDomain: null,
  setOverride: () => {},
};

export const WhiteLabelContext = createContext<WhiteLabelContextType>(defaultContext);

export const useWhiteLabel = () => useContext(WhiteLabelContext);

interface WhiteLabelProviderProps {
  children: ReactNode;
}

export function WhiteLabelProvider({ children }: WhiteLabelProviderProps) {
  const [config, setConfig] = useState<WhiteLabelConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDark, setIsDark] = useState(() => 
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  
  // Dev picker state
  const [allConfigs, setAllConfigs] = useState<WhiteLabelConfig[]>([]);
  const [overrideDomain, setOverrideDomain] = useState<string | null>(() => getWhiteLabelOverride());

  // Watch for dark mode changes from the DOM
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const loadConfig = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const fetchedConfig = await fetchWhiteLabelConfigWithOverride();
      setConfig(fetchedConfig);
      
      if (fetchedConfig) {
        applyWhiteLabelConfig(fetchedConfig);
      } else {
        // If no config, ensure we're using defaults (CSS variables will fall back)
        console.log('[WhiteLabel] No config loaded, using defaults');
      }
    } catch (err) {
      console.error('[WhiteLabel] Failed to load config:', err);
      setError(err instanceof Error ? err.message : 'Failed to load white-label config');
    } finally {
      setIsLoading(false);
    }
  };

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, []);

  // Re-apply config when theme changes (for logo switching)
  useEffect(() => {
    if (config) {
      applyWhiteLabelConfig(config);
    }
  }, [isDark, config]);

  const logo = getWhiteLabelLogo(config, isDark);
  const appName = config?.appName || 'de.iterate';
  const isWhiteLabeled = !!config && config.isActive;

  // Load all configs for dev picker
  const loadAllConfigs = async () => {
    const configs = await fetchAllWhiteLabelConfigs();
    setAllConfigs(configs);
  };

  // Set override domain and reload config
  const setOverride = (domain: string | null) => {
    setWhiteLabelOverride(domain);
    setOverrideDomain(domain);
    loadConfig(); // Reload with new override
  };

  const value: WhiteLabelContextType = {
    config,
    isLoading,
    error,
    logo,
    appName,
    isWhiteLabeled,
    refetch: loadConfig,
    allConfigs,
    loadAllConfigs,
    overrideDomain,
    setOverride,
  };

  return (
    <WhiteLabelContext.Provider value={value}>
      {children}
    </WhiteLabelContext.Provider>
  );
}
